import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { UnorderedList, ListItem } from "@vds/lists";
import { Text } from '@vds/core';
import { formatPriceString } from "utils/commonUtils";
import { isMobile } from "react-device-detect";
import { Badge } from "@vds/core";
import { Icon } from "@vds/icons";
import { RadioBox } from '@vbg-ds/core';

const ProtectionPlanTile = ({
  planData,
  isRecommended,
  selectedFeature,
  aemContent,
  onClick,
  isEdit = false,
  packageId,
  fromBYODMonarch,
  sno
}) => {
  const { t } = useTranslation(["pdp"]);
  const accountLevelCodes = t(
    "device-pdp.pdpProtectionContent.accountLevelCodes",
    { returnObjects: true }
  );
  const featureCode = planData?.billingCode;
  const isAccountLevel = accountLevelCodes.includes(planData?.spoCategoryCode);
  const planCartInfo = {
    displayName: aemContent?.displayName,
    featureCode,
    featureType: planData?.spo ? "SPO" : "SFO",
    featureLevel: isAccountLevel ? "A" : "L",
    quantity: "1",
  };
  const isSelected = !isEdit ? (featureCode === selectedFeature?.featureCode)
    : selectedFeature !== null && selectedFeature && packageId !== null
      && packageId && selectedFeature[packageId] ? (featureCode === selectedFeature[packageId]['billingCode']) : false;

  return (
    <TileRadioBox
      // width={isMobile ? "328px" : "525px"}
      onChange={() => onClick(planCartInfo)}
      selected={isSelected}
      data-track={fromBYODMonarch ? `Device | ${sno} | Device protection | ${planData?.name}` : aemContent?.displayName ?? planData?.name}>
      {!!isRecommended && (
        fromBYODMonarch ?
          <Badge backgroundColor="#E10014" textColor="#FFFFFF" children="Recommended" />
          :
          <div style={{ marginBottom: "9px", marginTop: "4px", paddingLeft: "0" }}>
            <Text kind="body" size="small" color="#EE0000">
              {t("device-pdp.pdpProtectionContent.recommendedText", { ns: "pdp" })}
            </Text>
          </div>
      )}
      <TopSection fromBYODMonarch={fromBYODMonarch}>
        {fromBYODMonarch ? <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
          <Text kind="title" size="xsmall">{aemContent?.displayName ?? planData?.name}</Text>
          {/* {isSelected && <IconCont>
              <Icon name="checkmark" size="small" color="#FFFFFF" />
          </IconCont>} */}
        </div> : 
          <Text kind="title" size="xsmall">{aemContent?.displayName ?? planData?.name}</Text>
        }
        <PriceContainer isAccountLevel={isAccountLevel} isMobile={isMobile}>
          <Text kind="title" size="xsmall" bold>
            <div className="priceTitle">
              {formatPriceString(planData?.retailPrice)}
            </div>
          </Text>
          {Math.sign(planData?.retailPrice) === 1 && (
            fromBYODMonarch ?
              <div>
                <Text kind="micro" color="#716F6D">
                  {isAccountLevel
                    ? t("device-pdp.pdpProtectionContent.monthPerAccount", { ns: "pdp" })
                    : t("device-pdp.pdpProtectionContent.linePerMonth", { ns: "pdp" })}
                </Text>
              </div> :
              <PerMonthWrapper>
                <Text kind="micro">
                  {isAccountLevel
                    ? t("device-pdp.pdpProtectionContent.monthPerAccount", { ns: "pdp" })
                    : t("device-pdp.pdpProtectionContent.linePerMonth", { ns: "pdp" })}
                </Text>
              </PerMonthWrapper>
          )}
        </PriceContainer>
      </TopSection>
      {!!aemContent?.subtext && (
        <div style={{ paddingLeft: "0" }}>
          <Text kind="body" size="small">{aemContent.subtext}</Text>
        </div>
      )}
      {!!aemContent?.detailBullets?.length && (
        <BottomSection>
          <Text kind="body" size="small" bold>
            {aemContent?.detailHeading}
          </Text>
          <UnorderedList>
            {aemContent?.detailBullets?.map((i) => (
              <ListItem size="bodySmall" spacing="compact">
                <span
                  dangerouslySetInnerHTML={{
                    __html: i,
                  }}
                />
              </ListItem>
            ))}
          </UnorderedList>
        </BottomSection>
      )}
    </TileRadioBox>
  );
};

const IconCont = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #000000;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const TileRadioBox = styled(RadioBox)`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding:24px;
  gap: 8px;
  width: 100%;
  background: #ffffff;
  border: 1px solid #d8dada;
  border-radius: 8px;
  border-width: ${(props) => (props?.isSelected ? "2px" : "1px")};
  border-color: ${(props) => (props?.isSelected ? "#000000" : "#d8dada")};
  cursor: pointer;
  pointer-events: auto;
  :hover {
    border-color: #000000;
  }
  @media (max-width: 525px) {
    padding: 16px;
  }
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.fromBYODMonarch ? 'column' : 'column')};
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 0;
  gap: ${(props) => (props.fromBYODMonarch ? '0.5rem' : '0.5rem')};
  @media (max-width: 525px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 0px 0px 4px;
  gap: 4px;

  .priceTitle {
  position: relative !important;
    top: ${(props) => (props?.isMobile ? ("0.1em !important") : (props?.isAccountLevel ? "-0.6em !important" : "-0.25em !important"))};
  }
`;

const PerMonthWrapper = styled.div`
  width: 59px;
  @media (max-width: 525px) {
    width: 100%;
  }
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 4px;
  sup {
    font-size: 0.4rem;
    vertical-align: super;
    line-height: 0;
  }
`;

export default ProtectionPlanTile;
